import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, CircularProgress, IconButton, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import SearchableSelect from '../common/SearchableSelect';
import SalespersonSelect from './scope/SalespersonSelect';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import ScrollableTable from '../common/ScrollableTable';
import { SalesOrderService, fetchAllPages } from '../../services/salesOrder.service';
import SalesCatalogueSelect from './SalesCatalogueSelect';   // FW-2A
import KitAvailabilityHint from './KitAvailabilityHint';   // F6c
import { discountError, discountPatch, previewLine, shownDiscountAmount } from '../../utils/salesLineDiscount';   // FW-2A
import { CustomerService } from '../../services/customer.service';
import { Customer } from '../../interfaces/sales/customer.interface';
import { OrderLineRequest } from '../../interfaces/sales/salesOrder.interface';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { formatMoney } from '../../utils/format';
import { toLocalIsoDate } from '../../utils/dateUtils';
import { helpWhy } from '../../help/why';
import DocumentCurrencyFields from '../common/DocumentCurrencyFields';   // G5b
import { catalogueUnitPrice, foreignLineDiscount, formatFc, isForeignCurrency, repriceCatalogueLines, toastSaved } from '../../utils/fcDocuments';   // G5b

interface Row extends Omit<OrderLineRequest, 'quantity' | 'unitPrice' | 'taxRatePct'> {
  key: number; itemLabel?: string; itemKind?: string; quantity: number; unitPrice: number; taxRatePct: number;
  catalogueBdtPrice?: number | null;   // polish 4: set while the price still comes from the catalogue pick
}
let seq = 0;
const blank = (): Row => ({ key: ++seq, description: '', quantity: 1, unitPrice: 0, taxRatePct: 0 });
const num = (n: number) => (Number.isFinite(n) ? n : 0);

const SalesOrderForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerId, setCustomerId] = useState('');
  const canPickSalesperson = usePermission(PERMISSIONS.SALES_REASSIGN);
  const [salespersonId, setSalespersonId] = useState<string | null>(null);
  const [orderDate, setOrderDate] = useState(toLocalIsoDate());
  const [requested, setRequested] = useState('');
  const [notes, setNotes] = useState('');
  const [rows, setRows] = useState<Row[]>([blank()]);
  const [defaultStoreId, setDefaultStoreId] = useState<string | null>(null);   // F6c: kit availability hint
  // ---- G5b: order currency; the rate is indicative (the invoice resolves its own); fixed once the order is confirmed ----
  const [currency, setCurrency] = useState('BDT');
  const [fxRate, setFxRate] = useState<number | ''>('');
  const [status, setStatus] = useState('DRAFT');
  const foreign = isForeignCurrency(currency);
  const currencyLocked = status !== 'DRAFT';
  // ---- end G5b ----

  useEffect(() => {
    SalesOrderService.getSettings(institutionId).then(s => setDefaultStoreId(s.defaultStoreId ?? null)).catch(() => undefined);
  }, [institutionId]);

  // Each list loads on its own: one failing call must not blank the others.
  useEffect(() => {
    fetchAllPages<Customer>((p, n) => CustomerService.listByInstitution(institutionId, p, n, id ? 'ALL' : undefined)) // FW-1: edit keeps a now-inactive current customer
      .then(setCustomers)
      .catch(e => toast.showApiError(e, 'Failed to load customers'));
  }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!id) { setLoading(false); return; }
    SalesOrderService.get(id)
      .then(o => {
        setCustomerId(o.customerId); setOrderDate(o.orderDate); setRequested(o.requestedDeliveryDate ?? ''); setNotes(o.notes ?? '');
        const fc = isForeignCurrency(o.currencyCode);   // G5b: a foreign order is edited in its currency
        setCurrency(o.currencyCode ?? 'BDT'); setFxRate(fc && o.exchangeRate != null ? o.exchangeRate : ''); setStatus(o.status);
        setRows(o.lines.map(l => ({
          key: ++seq, id: l.id, itemId: l.itemId, itemLabel: l.itemCode ? `${l.itemCode} - ${l.itemName}` : undefined,
          itemKind: l.itemKind ?? undefined,   // F6c: an edited kit line shows its availability hint too
          description: l.description, quantity: l.quantity, unitPrice: fc && l.foreignUnitPrice != null ? l.foreignUnitPrice : l.unitPrice,
          taxRatePct: l.taxRatePct, discountPct: l.discountPct ?? null,
          discountAmount: l.discountPct != null ? null
            : fc ? (foreignLineDiscount(l.quantity, l.foreignUnitPrice, l.foreignAmount) || null)
            : (Number(l.discountAmount) > 0 ? l.discountAmount : null), taxCodeId: l.taxCodeId, revenueAccountId: l.revenueAccountId, segmentId: l.segmentId, storeId: l.storeId,
        })));
      })
      .catch(e => toast.showApiError(e, 'Failed to load the order'))
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const patch = (key: number, p: Partial<Row>) => setRows(rs => rs.map(r => (r.key === key ? { ...r, ...p } : r)));
  const total = rows.reduce((s, r) => s + previewLine(r).net * (1 + num(r.taxRatePct) / 100)   /* FW-2A: net of discount */, 0);
  const invalid = (foreign && !(Number(fxRate) > 0)) || !customerId || rows.length === 0 || rows.some(r => !r.description.trim() || !(r.quantity > 0) || !(r.unitPrice >= 0) || !!discountError(r));

  const submit = async () => {
    const dto = {
      customerId, ...(!id && canPickSalesperson && salespersonId ? { salespersonId } : {}), orderDate, requestedDeliveryDate: requested || null, notes: notes || undefined,
      currencyCode: currency, exchangeRate: foreign ? Number(fxRate) : null,   // G5b: BDT is sent too, so a foreign draft converts on purpose
      lines: rows.map(({ key, itemLabel, itemKind, catalogueBdtPrice, ...l }) => ({ ...l, taxRatePct: num(l.taxRatePct), discountPct: l.discountPct ?? undefined, discountAmount: l.discountPct == null ? (l.discountAmount ?? undefined) : undefined })),
    };
    try {
      setSaving(true);
      const saved = id ? await SalesOrderService.update(id, dto) : await SalesOrderService.create(institutionId, dto);
      toastSaved(toast, 'Order saved', saved.warnings);   // G5b
      navigate(`/sales/orders/${saved.id}`);
    } catch (e) {
      toast.showApiError(e, 'Failed to save the order');
    } finally {
      setSaving(false);
    }
  };

  const saveWhy = saving ? null
    : !customerId ? 'Choose a customer first.'
    : rows.length === 0 ? 'Add at least one line.'
    : rows.some(r => !r.description.trim() || !(r.quantity > 0) || !(r.unitPrice >= 0)) ? 'Every line needs a description, a quantity above zero and a price.'
    : rows.some(r => !!discountError(r)) ? 'A line discount must be between 0 and 100 percent and not more than the line amount.'
    : (foreign && !(Number(fxRate) > 0)) ? 'Enter an exchange rate above zero.'
    : null;

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  return (
    <Box sx={{ p: 3 }} data-help-page="sales.orderForm.page">
      <Typography variant="h5" sx={{ mb: 2 }}>{id ? 'Edit Sales Order' : 'New Sales Order'}</Typography>
      <Paper data-help-section="sales.orderForm.header" sx={{ p: 3, mb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <SearchableSelect data-help="sales.orderForm.customer" label="Customer" required options={customers} value={customerId || null} onChange={v => setCustomerId(v)} getOptionLabel={c => c.name} />
        {!id && canPickSalesperson && (
          <SalespersonSelect data-help="sales.orderForm.field.salesperson" size="medium" value={salespersonId} onChange={setSalespersonId} helperText="Leave empty for the default owner (the customer's salesperson, else you)." />
        )}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <TextField data-help="sales.orderForm.orderDate" label="Order date" type="date" value={orderDate} onChange={e => setOrderDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 200px', maxWidth: 260 }} />
          <TextField data-help="sales.orderForm.requested" label="Requested delivery" type="date" value={requested} onChange={e => setRequested(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 200px', maxWidth: 260 }} />
        </Box>
        <DocumentCurrencyFields institutionId={institutionId ?? ''} currency={currency} rate={fxRate} asOf={orderDate}
          currencyProps={{ 'data-help': 'sales.orderForm.field.currency' }} rateProps={{ 'data-help': 'sales.orderForm.field.rate' }}
          disabled={currencyLocked} onChange={(c, r) => { setCurrency(c); setFxRate(r); setRows(rs => repriceCatalogueLines(rs, c, r)); }} />
        {foreign && (
          <Typography variant="caption" color="text.secondary">
            {currencyLocked ? 'The currency and rate of a confirmed order are fixed. ' : ''}
            Prices are in {currency}; the rate is indicative: the invoice uses the rate of its own date.
          </Typography>
        )}
        <TextField data-help="sales.orderForm.notes" label="Notes" value={notes} onChange={e => setNotes(e.target.value)} multiline minRows={2} />
      </Paper>
      <Paper data-help-section="sales.orderForm.lines" sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6">Lines</Typography>
          <Button data-help="sales.orderForm.addLine" startIcon={<Add />} onClick={() => setRows(rs => [...rs, blank()])}>Add line</Button>
        </Box>
        <ScrollableTable>
          <Table size="small">
            <TableHead>
              <TableRow><TableCell data-help="sales.orderForm.col.item">Item</TableCell><TableCell data-help="sales.orderForm.col.description">Description</TableCell><TableCell data-help="sales.orderForm.col.qty">Qty</TableCell><TableCell data-help="sales.orderForm.col.unitPrice">{foreign ? `Unit price (${currency})` : 'Unit price'}</TableCell><TableCell data-help="sales.orderForm.col.discPct">Disc %</TableCell><TableCell data-help="sales.orderForm.col.discAmount">{foreign ? `Disc amount (${currency})` : 'Disc amount'}</TableCell><TableCell data-help="sales.orderForm.col.taxPct">Tax %</TableCell><TableCell /></TableRow>
            </TableHead>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.key}>
                  <TableCell sx={{ minWidth: 200 }}>
                    <SalesCatalogueSelect data-help="sales.orderForm.col.item"
                      label="Product / service (blank = free-text service)" value={r.itemId ?? null} valueLabel={r.itemLabel}
                      onChange={(v, opt) => patch(r.key, !v || !opt ? { itemId: null, itemLabel: undefined, itemKind: undefined, catalogueBdtPrice: null } : {
                        itemId: v, itemLabel: `${opt.code} - ${opt.name}`, itemKind: opt.kind, description: r.description || opt.name,
                        ...(opt.salePrice != null ? { unitPrice: catalogueUnitPrice(opt.salePrice, currency, fxRate), catalogueBdtPrice: opt.salePrice } : {}),   // polish 4
                        ...(opt.revenueAccountId ? { revenueAccountId: opt.revenueAccountId } : {}),
                      })}
                    />
                    {r.itemKind === 'KIT' && r.itemId && (
                      <KitAvailabilityHint institutionId={institutionId} kitItemId={r.itemId} storeId={r.storeId ?? defaultStoreId}
                        where={r.storeId ? 'in the line store' : 'in the default store'} />
                    )}{/* F6c */}
                  </TableCell>
                  <TableCell data-help="sales.orderForm.col.description"><TextField size="small" value={r.description} onChange={e => patch(r.key, { description: e.target.value })} /></TableCell>
                  <TableCell data-help="sales.orderForm.col.qty"><TextField size="small" type="number" sx={{ width: 90 }} value={Number.isFinite(r.quantity) ? r.quantity : ''} onChange={e => patch(r.key, { quantity: e.target.value === '' ? NaN : Number(e.target.value) })} /></TableCell>
                  <TableCell data-help="sales.orderForm.col.unitPrice"><TextField size="small" type="number" sx={{ width: 110 }} value={Number.isFinite(r.unitPrice) ? r.unitPrice : ''} inputProps={{ 'aria-label': 'Unit price' }} onChange={e => patch(r.key, { unitPrice: e.target.value === '' ? NaN : Number(e.target.value), catalogueBdtPrice: null })} /></TableCell>
                  <TableCell data-help="sales.orderForm.col.discPct"><TextField size="small" type="number" sx={{ width: 80 }} inputProps={{ 'aria-label': 'Disc %', step: 'any', min: 0, max: 100 }} value={r.discountPct ?? ''} error={!!discountError(r)} helperText={discountError(r) ?? undefined} onChange={e => patch(r.key, discountPatch('pct', e.target.value))} /></TableCell>
                  <TableCell data-help="sales.orderForm.col.discAmount"><TextField size="small" type="number" sx={{ width: 100 }} inputProps={{ 'aria-label': 'Disc amount', step: 'any', min: 0 }} value={shownDiscountAmount(r)} onChange={e => patch(r.key, discountPatch('amount', e.target.value))} /></TableCell>
                  <TableCell data-help="sales.orderForm.col.taxPct"><TextField size="small" type="number" sx={{ width: 80 }} value={Number.isFinite(r.taxRatePct) ? r.taxRatePct : ''} onChange={e => patch(r.key, { taxRatePct: e.target.value === '' ? NaN : Number(e.target.value) })} /></TableCell>
                  <TableCell><IconButton size="small" color="error" data-help="sales.orderForm.removeLine" aria-label="remove line" disabled={rows.length === 1} onClick={() => setRows(rs => rs.filter(x => x.key !== r.key))}><Delete /></IconButton></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollableTable>
        <Typography sx={{ mt: 2, textAlign: 'right' }}>Estimated total: <b>{foreign ? formatFc(currency, total) : formatMoney(total)}</b></Typography>
      </Paper>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button data-help="sales.orderForm.save" {...helpWhy(saveWhy ? { kind: 'status', message: saveWhy } : null)} variant="contained" disabled={invalid || saving} onClick={submit}>Save</Button>
        <Button data-help="sales.orderForm.cancel" onClick={() => navigate(id ? `/sales/orders/${id}` : '/sales/orders')}>Cancel</Button>
      </Box>
    </Box>
  );
};

export default SalesOrderForm;
